import { useState, useRef } from "react";
import { motion } from "motion/react";
import {
  TrendingUp,
  PoundSterling,
  Clock,
  MapPin,
  Check,
  Upload,
  FileText,
  Send,
  LayoutDashboard,
  MessageSquare,
  CreditCard,
  FileSignature,
  Users,
  CalendarDays,
  Building2,
  Handshake,
  Megaphone,
  Target,
  Palette,
  BarChart3,
  Sparkles,
  Film,
  Search,
} from "lucide-react";
import PageGlow from "@/components/PageGlow";
import HeroWave from "@/components/HeroWave";
import LazyDemoSection from "@/components/portal/LazyDemoSection";

const GRAD = "#5048E5";
const CORAL = "hsl(14 100% 57%)";
const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const border = "hsl(var(--border))";
const card = "hsl(var(--card))";

const GRAD_TEXT: React.CSSProperties = {
  background: `linear-gradient(135deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)`,
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" as const } },
};

const SALES_PERKS = [
  { icon: PoundSterling, label: "Commission based", sub: "Uncapped earnings on every client you win" },
  { icon: Clock, label: "Flexible hours", sub: "Work around your life, results over clock-watching" },
  { icon: MapPin, label: "Fully remote", sub: "UK-based, work from anywhere" },
  { icon: TrendingUp, label: "Grow with us", sub: "Early team member with room to progress" },
];

const CONSULTANT_PERKS = [
  { icon: PoundSterling, label: "60% commission", sub: "You keep 60% on every client you bring and manage" },
  { icon: Building2, label: "Build your own book", sub: "Win your own clients and grow your personal base" },
  { icon: Handshake, label: "We handle the rest", sub: "Billing, contracts, portal access and brand support included" },
  { icon: MapPin, label: "Fully remote", sub: "UK-based, work from anywhere with your own schedule" },
];

const SALES_SKILLS = [
  "Confident communicator — comfortable on the phone, on video and over email",
  "A natural at building relationships and earning trust quickly",
  "Self-motivated and target-driven — you thrive on hitting goals",
  "Strong computer literacy — confident navigating software, CRMs and digital tools",
  "Comfortable with AI-assisted workflows — you use AI to speed up research, outreach and follow-ups",
  "Organised — able to manage your own pipeline of leads",
  "A genuine interest in helping small businesses grow",
];

const CONSULTANT_SKILLS = [
  "Proven experience in web design, app development, marketing or a related digital field",
  "Hands-on with AI creation tools — confident using AI for web builds, app prototyping and automated workflows",
  "Comfortable managing client relationships end-to-end — from discovery to delivery",
  "Self-starter who can build a client base independently",
  "Happy using project-management and client-portal tools (or willing to learn)",
  "Professional and reliable — clients stay because they trust you",
  "Wants the freedom of self-employment with the backing of an established brand",
];

const MARKETING_PERKS = [
  { icon: PoundSterling, label: "Commission based", sub: "Uncapped earnings on every campaign you run and client you win" },
  { icon: Megaphone, label: "Full ad stack", sub: "Run Google Ads, Meta Ads and TikTok campaigns for real clients" },
  { icon: BarChart3, label: "Data & reporting", sub: "Track performance, optimise spend and prove ROI with clear reports" },
  { icon: MapPin, label: "Fully remote", sub: "UK-based, work from anywhere with flexible hours" },
];

const MARKETING_SKILLS = [
  "Strong knowledge of Google Ads, Meta Ads and TikTok Ads — from setup to optimisation",
  "Experience launching and managing paid campaigns with clear ROI targets",
  "Familiar with the sports advertising space (e.g. bowls scoreboard ads, bowls mat ads) or eager to learn",
  "Hands-on with AI-assisted marketing tools — you use platforms like Claude and Ryze to speed up copywriting, creative ideation and campaign workflows",
  "Analytical mindset — comfortable reading reports, adjusting bids and improving CTR",
  "Self-motivated and organised — you can manage multiple campaigns and deadlines",
];

const CONTENT_PERKS = [
  { icon: Palette, label: "Graphic design", sub: "Create eye-catching social assets, banners and brand visuals" },
  { icon: Film, label: "Videography", sub: "Shoot and edit video content for ads, social and client projects" },
  { icon: Search, label: "SEO, AEO & GEO", sub: "Create content engineered to rank in search, answer engines and generative AI results" },
  { icon: Sparkles, label: "Creative direction", sub: "Own the visual storytelling across all channels and campaigns" },
  { icon: MapPin, label: "Fully remote", sub: "UK-based, work from anywhere with flexible hours" },
];

const CONTENT_SKILLS = [
  "Strong graphic design skills — confident creating social assets, banners, ads and brand visuals",
  "Videography and video editing — able to shoot and edit content for campaigns and client projects",
  "Experienced with AI-assisted content workflows — using AI tools to speed up design, editing and creative production",
  "SEO, AEO & GEO content creation — you can write and optimise content that ranks in search engines, answer engines and generative AI results to drive organic traffic",
  "A good eye for brand storytelling — you know how to make content that stops the scroll",
  "Comfortable with content tools — Adobe Creative Suite, Canva, CapCut or equivalent",
  "Self-motivated and organised — you can manage a content calendar and hit deadlines",
  "Keen interest in digital marketing — you understand how your content feeds into campaigns and conversions",
];

const ADMIN_TOOLS = [
  { icon: LayoutDashboard, label: "Client dashboard", sub: "Track every client's progress, milestones and status at a glance." },
  { icon: MessageSquare, label: "Built-in messaging", sub: "Chat, calls and file sharing with clients — all in one place." },
  { icon: CreditCard, label: "Invoicing & payments", sub: "Create invoices and take card payments that mark themselves paid." },
  { icon: FileSignature, label: "Service agreements", sub: "Send, e-sign and store agreements without leaving the portal." },
  { icon: CalendarDays, label: "Calendar & bookings", sub: "Connect your calendar so clients book calls around your availability." },
  { icon: Users, label: "Client management", sub: "Notes, documents and a full history for every account you manage." },
];

type RoleTab = "sales" | "marketing" | "content" | "consultant";
type Status = "idle" | "sending" | "success" | "error";

export default function CareersPage() {
  const [roleTab, setRoleTab] = useState<RoleTab>("sales");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError("Please attach your CV.");
      return;
    }
    setStatus("sending");
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("phone", form.phone);
      data.append("message", form.message);
      const roleLabel =
        roleTab === "sales" ? "Sales Executive (Commission based)" :
        roleTab === "consultant" ? "Subcontractor Consultant (60% commission)" :
        roleTab === "content" ? "Digital Content Producer (Commission based)" :
        "Marketing Executive (Commission based)";
      data.append("role", roleLabel);
      data.append("cv", file);

      const res = await fetch("/api/careers/apply", { method: "POST", body: data });
      const json = (await res.json()) as { success: boolean; error?: string };
      if (!res.ok || !json.success) throw new Error(json.error || "Something went wrong.");
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong.");
    }
  };

  const inputStyle: React.CSSProperties = {
    background: "hsl(var(--background))",
    border: `1px solid ${border}`,
    color: fg,
    fontFamily: "Plus Jakarta Sans, sans-serif",
  };

  const roleContent = () => {
    if (roleTab === "sales") {
      return (
        <>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
                Sales Executive
              </h2>
              <p className="mt-1 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Open role · Apply below
              </p>
            </div>
            <span
              className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-sm font-bold"
              style={{ background: "hsla(14,100%,57%,0.12)", color: "hsl(14 100% 57%)", fontFamily: "Plus Jakarta Sans, sans-serif" }}
            >
              <PoundSterling size={15} /> Commission based
            </span>
          </div>
          <p className="mt-5 max-w-2xl text-base leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            As our Sales Executive you'll be the first point of contact for small businesses who want to grow online. You'll
            introduce our services, understand what each business needs, and bring new clients on board — earning
            commission on every deal you close.
          </p>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {SALES_PERKS.map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex items-start gap-3 rounded-2xl p-4" style={{ background: "hsl(var(--background))", border: `1px solid ${border}` }}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: "hsla(243,75%,59%,0.12)" }}>
                  <Icon size={18} style={{ color: GRAD }} />
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{label}</p>
                  <p className="mt-0.5 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{sub}</p>
                </div>
              </div>
            ))}
          </div>
          <h3 className="mt-9 mb-4 text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
            What we're looking for
          </h3>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {SALES_SKILLS.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: "hsla(243,75%,59%,0.14)" }}>
                  <Check size={13} style={{ color: GRAD }} />
                </span>
                <span className="text-sm leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{s}</span>
              </li>
            ))}
          </ul>
        </>
      );
    }

    if (roleTab === "consultant") {
      return (
        <>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
                Subcontractor Consultant
              </h2>
              <p className="mt-1 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Open role · Apply below
              </p>
            </div>
            <span
              className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-sm font-bold"
              style={{ background: "hsla(14,100%,57%,0.12)", color: CORAL, fontFamily: "Plus Jakarta Sans, sans-serif" }}
            >
              <PoundSterling size={15} /> 60% commission
            </span>
          </div>
          <p className="mt-5 max-w-2xl text-base leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Work under the Digitally Baffled brand while building your own client base. You bring in the clients, manage
            them end-to-end through our portal, and keep 60% of every bill. We handle contracts, invoicing, payments
            and the platform — you focus on delivering great work.
          </p>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CONSULTANT_PERKS.map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex items-start gap-3 rounded-2xl p-4" style={{ background: "hsl(var(--background))", border: `1px solid ${border}` }}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: "hsla(243,75%,59%,0.12)" }}>
                  <Icon size={18} style={{ color: GRAD }} />
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{label}</p>
                  <p className="mt-0.5 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{sub}</p>
                </div>
              </div>
            ))}
          </div>
          <h3 className="mt-9 mb-4 text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
            What we're looking for
          </h3>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {CONSULTANT_SKILLS.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: "hsla(243,75%,59%,0.14)" }}>
                  <Check size={13} style={{ color: GRAD }} />
                </span>
                <span className="text-sm leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{s}</span>
              </li>
            ))}
          </ul>
        </>
      );
    }

    if (roleTab === "content") {
      return (
        <>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
                Digital Content Producer
              </h2>
              <p className="mt-1 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Open role · Apply below
              </p>
            </div>
            <span
              className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-sm font-bold"
              style={{ background: "hsla(14,100%,57%,0.12)", color: "hsl(14 100% 57%)", fontFamily: "Plus Jakarta Sans, sans-serif" }}
            >
              <PoundSterling size={15} /> Commission based
            </span>
          </div>
          <p className="mt-5 max-w-2xl text-base leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            As our Digital Content Producer you'll own the creative output across our brand and client campaigns — from
            graphic design and social assets to videography and video editing. You'll also create SEO, AEO and GEO
            content that drives organic traffic, helping clients rank in search engines, answer engines and generative AI
            results. You create the visuals and written content that make ads convert, feeds stand out, and brands memorable.
          </p>
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CONTENT_PERKS.map(({ icon: Icon, label, sub }) => (
              <div key={label} className="flex items-start gap-3 rounded-2xl p-4" style={{ background: "hsl(var(--background))", border: `1px solid ${border}` }}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: "hsla(243,75%,59%,0.12)" }}>
                  <Icon size={18} style={{ color: GRAD }} />
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{label}</p>
                  <p className="mt-0.5 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{sub}</p>
                </div>
              </div>
            ))}
          </div>
          <h3 className="mt-9 mb-4 text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
            What we're looking for
          </h3>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {CONTENT_SKILLS.map((s) => (
              <li key={s} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: "hsla(243,75%,59%,0.14)" }}>
                  <Check size={13} style={{ color: GRAD }} />
                </span>
                <span className="text-sm leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{s}</span>
              </li>
            ))}
          </ul>
        </>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
              Marketing Executive
            </h2>
            <p className="mt-1 text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Open role · Apply below
            </p>
          </div>
          <span
            className="inline-flex w-fit items-center gap-2 rounded-full px-4 py-2 text-sm font-bold"
            style={{ background: "hsla(14,100%,57%,0.12)", color: "hsl(14 100% 57%)", fontFamily: "Plus Jakarta Sans, sans-serif" }}
          >
            <PoundSterling size={15} /> Commission based
          </span>
        </div>
        <p className="mt-5 max-w-2xl text-base leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          As our Marketing Executive you'll plan, launch and optimise paid campaigns across Google Ads, Meta and TikTok.
          You'll also spearhead our presence in the sports advertising space, from bowls scoreboard ads to bowls mat ads,
          driving measurable results through data-backed strategy.
        </p>
        <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {MARKETING_PERKS.map(({ icon: Icon, label, sub }) => (
            <div key={label} className="flex items-start gap-3 rounded-2xl p-4" style={{ background: "hsl(var(--background))", border: `1px solid ${border}` }}>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ background: "hsla(243,75%,59%,0.12)" }}>
                <Icon size={18} style={{ color: GRAD }} />
              </div>
              <div>
                <p className="text-sm font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{label}</p>
                <p className="mt-0.5 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{sub}</p>
              </div>
            </div>
          ))}
        </div>
        <h3 className="mt-9 mb-4 text-lg font-bold" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
          What we're looking for
        </h3>
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {MARKETING_SKILLS.map((s) => (
            <li key={s} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ background: "hsla(243,75%,59%,0.14)" }}>
                <Check size={13} style={{ color: GRAD }} />
              </span>
              <span className="text-sm leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{s}</span>
            </li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden py-12 md:py-20" style={{ background: "hsl(var(--background))" }}>
        <HeroWave fadeBottom={58} bleed="both" />
        <PageGlow />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <h1 className="mb-4 text-4xl font-bold md:text-5xl" style={{ fontFamily: "Outfit, sans-serif", color: fg, lineHeight: 1.08 }}>
              Build your career <span style={GRAD_TEXT}>with Digitally Baffled</span>
            </h1>
            <p className="text-base leading-relaxed md:text-lg" style={{ fontFamily: "Plus Jakarta Sans, sans-serif", color: fgMuted }}>
              We're hiring in four directions — join us as a commission-based Sales Executive, run paid campaigns as our
              Marketing Executive, create standout visuals as a Digital Content Producer, or subcontract through us as a
              consultant and build your own client base.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Role tabs ── */}
      <section className="mx-auto max-w-5xl px-6 py-10 md:py-14">
        <div className="mx-auto mb-8 flex max-w-xl rounded-full p-1" style={{ background: "hsl(var(--background))", border: `1px solid ${border}` }}>
          <button
            type="button"
            onClick={() => setRoleTab("sales")}
            className="flex-1 rounded-full px-3 py-2.5 text-sm font-bold transition-colors"
            style={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              background: roleTab === "sales" ? GRAD : "transparent",
              color: roleTab === "sales" ? "#fff" : fgMuted,
            }}
          >
            Sales
          </button>
          <button
            type="button"
            onClick={() => setRoleTab("marketing")}
            className="flex-1 rounded-full px-3 py-2.5 text-sm font-bold transition-colors"
            style={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              background: roleTab === "marketing" ? GRAD : "transparent",
              color: roleTab === "marketing" ? "#fff" : fgMuted,
            }}
          >
            Marketing
          </button>
          <button
            type="button"
            onClick={() => setRoleTab("content")}
            className="flex-1 rounded-full px-3 py-2.5 text-sm font-bold transition-colors"
            style={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              background: roleTab === "content" ? GRAD : "transparent",
              color: roleTab === "content" ? "#fff" : fgMuted,
            }}
          >
            Content
          </button>
          <button
            type="button"
            onClick={() => setRoleTab("consultant")}
            className="flex-1 rounded-full px-3 py-2.5 text-sm font-bold transition-colors"
            style={{
              fontFamily: "Plus Jakarta Sans, sans-serif",
              background: roleTab === "consultant" ? GRAD : "transparent",
              color: roleTab === "consultant" ? "#fff" : fgMuted,
            }}
          >
            Consultant
          </button>
        </div>

        <motion.div
          key={roleTab}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="rounded-3xl p-7 md:p-10"
          style={{ background: card, border: `1px solid ${border}` }}
        >
          {roleContent()}
        </motion.div>
      </section>

      {/* ── Portal demo ── */}
      <section className="mx-auto max-w-6xl px-6 py-10 md:py-14">
        <div className="mx-auto mb-8 max-w-2xl text-center">
          <h2 className="mb-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
            The tools you'd <span style={GRAD_TEXT}>work with every day</span>
          </h2>
          <p className="text-base leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            We run everything through our own portal — from first conversation to signed client. Have a play with the live
            demo below to get a feel for how we manage our clients.
          </p>
        </div>

        <div className="mb-10 flex justify-center">
          <LazyDemoSection
            load={() => import("@/components/portal/PortalDemo")}
            className="w-full max-w-5xl"
            minHeight="min(760px, 82vh)"
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ADMIN_TOOLS.map(({ icon: Icon, label, sub }) => (
            <div key={label} className="rounded-2xl p-5" style={{ background: card, border: `1px solid ${border}` }}>
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "hsla(243,75%,59%,0.12)" }}>
                <Icon size={18} style={{ color: GRAD }} />
              </div>
              <p className="text-sm font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{label}</p>
              <p className="mt-1 text-xs leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Application form ── */}
      <section className="mx-auto max-w-2xl px-6 py-10 md:py-16">
        <div className="rounded-3xl p-7 md:p-10" style={{ background: card, border: `1px solid ${border}` }}>
            <h2 className="mb-2 text-2xl font-bold md:text-3xl" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>
              Apply now
            </h2>
            <p className="mb-7 text-sm leading-relaxed" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Upload your CV and we'll be in touch. It goes straight to our hiring team.
            </p>

            {/* Role selector */}
            <div className="mb-6">
              <label className="mb-1.5 block text-xs font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Applying for</label>
              <div className="flex rounded-xl p-1" style={{ background: "hsl(var(--background))", border: `1px solid ${border}` }}>
                <button
                  type="button"
                  onClick={() => setRoleTab("sales")}
                  className="flex-1 rounded-lg px-2 py-2.5 text-xs font-bold transition-colors"
                  style={{
                    fontFamily: "Plus Jakarta Sans, sans-serif",
                    background: roleTab === "sales" ? GRAD : "transparent",
                    color: roleTab === "sales" ? "#fff" : fgMuted,
                  }}
                >
                  Sales
                </button>
                <button
                  type="button"
                  onClick={() => setRoleTab("marketing")}
                  className="flex-1 rounded-lg px-2 py-2.5 text-xs font-bold transition-colors"
                  style={{
                    fontFamily: "Plus Jakarta Sans, sans-serif",
                    background: roleTab === "marketing" ? GRAD : "transparent",
                    color: roleTab === "marketing" ? "#fff" : fgMuted,
                  }}
                >
                  Marketing
                </button>
                <button
                  type="button"
                  onClick={() => setRoleTab("content")}
                  className="flex-1 rounded-lg px-2 py-2.5 text-xs font-bold transition-colors"
                  style={{
                    fontFamily: "Plus Jakarta Sans, sans-serif",
                    background: roleTab === "content" ? GRAD : "transparent",
                    color: roleTab === "content" ? "#fff" : fgMuted,
                  }}
                >
                  Content
                </button>
                <button
                  type="button"
                  onClick={() => setRoleTab("consultant")}
                  className="flex-1 rounded-lg px-2 py-2.5 text-xs font-bold transition-colors"
                  style={{
                    fontFamily: "Plus Jakarta Sans, sans-serif",
                    background: roleTab === "consultant" ? GRAD : "transparent",
                    color: roleTab === "consultant" ? "#fff" : fgMuted,
                  }}
                >
                  Consultant
                </button>
              </div>
            </div>

          {status === "success" ? (
            <div className="rounded-2xl p-8 text-center" style={{ background: "hsla(243,75%,59%,0.08)", border: `1px solid ${border}` }}>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full" style={{ background: "hsla(243,75%,59%,0.16)" }}>
                <Check size={26} style={{ color: GRAD }} />
              </div>
              <h3 className="mb-2 text-xl font-bold" style={{ fontFamily: "Outfit, sans-serif", color: fg }}>Application sent!</h3>
              <p className="text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Thanks {form.name.split(" ")[0] || "for applying"} — we've received your CV and will be in touch soon.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Full name *</label>
                  <input required name="name" value={form.name} onChange={handleChange} placeholder="Jane Smith"
                    className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2" style={inputStyle} />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Email *</label>
                  <input required type="email" name="email" value={form.email} onChange={handleChange} placeholder="jane@email.com"
                    className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2" style={inputStyle} />
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} placeholder="07XXX XXXXXX"
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2" style={inputStyle} />
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Why you? (optional)</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} placeholder="Tell us a little about yourself…"
                  className="w-full resize-none rounded-xl px-4 py-3 text-sm outline-none focus:ring-2" style={inputStyle} />
              </div>

              {/* CV upload */}
              <div>
                <label className="mb-1.5 block text-xs font-bold" style={{ color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Your CV *</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="flex w-full items-center gap-3 rounded-xl px-4 py-3.5 text-left text-sm transition-colors"
                  style={{ ...inputStyle, borderStyle: "dashed" }}
                >
                  {file ? <FileText size={18} style={{ color: GRAD }} /> : <Upload size={18} style={{ color: fgMuted }} />}
                  <span style={{ color: file ? fg : fgMuted }}>
                    {file ? file.name : "Click to upload (PDF or Word, max 8 MB)"}
                  </span>
                </button>
              </div>

              {error && (
                <p className="text-sm" style={{ color: "hsl(14 100% 57%)", fontFamily: "Plus Jakarta Sans, sans-serif" }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={status === "sending"}
                className="mt-1 flex items-center justify-center gap-2 rounded-xl px-6 py-3.5 text-sm font-bold text-white transition-opacity disabled:opacity-60"
                style={{ background: GRAD, fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                <Send size={16} />
                {status === "sending" ? "Sending…" : "Send application"}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
